import {
  FilesApi,
  type Lead,
  LeadSource,
  LeadStatus,
  type ScanBusinessCardResultDto,
} from '@up2stay/up2promo-api-client';
import { Badge } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { PhoneInput } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Textarea } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { AlertCircle, CheckCircle2, Loader2, RotateCcw } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';
import { toast } from 'sonner';

import type { BusinessCardCaptureResult } from '@/core/components/leads/business-card-camera-modal';
import { LeadDuplicateResolutionDialog } from '@/core/components/leads/lead-duplicate-resolution-dialog';
import {
  LEAD_EVENT_CAPTURE_NONE,
  LeadEventCapturePicker,
} from '@/core/components/leads/lead-event-capture-picker';
import {
  LeadAttachmentKind,
  type LeadAttachmentKindValue,
} from '@/core/constants/lead-attachment-kind';
import { useActiveCaptureEvents } from '@/core/hooks/use-active-capture-events';
import { useLeadDuplicateResolution } from '@/core/hooks/use-lead-duplicate-resolution';
import { useLocale } from '@/core/hooks/use-locale';
import {
  triggerErrorHaptic,
  triggerSuccessHaptic,
} from '@/core/lib/dnd-haptics';
import { useDictionary } from '@/core/providers/dictionary-provider';
import {
  base64ToFile,
  extensionFromMimeType,
} from '@/core/utils/base64-to-file';

type ScanFieldKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'companyName'
  | 'jobTitle'
  | 'website'
  | 'address';

type FieldLabels = Record<ScanFieldKey | 'notes', string>;
type ScanLabels = {
  title: string;
  description: string;
  found: string;
  missing: string;
  requiredHint: string;
  save: string;
  saving: string;
  retake: string;
  cancel: string;
  firstNameRequired: string;
  saveFailed: string;
  uploadFailed: string;
  saveWithoutAttachment: string;
};

export interface BusinessCardScanReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scanResult: ScanBusinessCardResultDto | null;
  capture: BusinessCardCaptureResult | null;
  existingLeads?: Lead[];
  profileId?: string | null;
  workspaceId?: string | null;
  eventIds?: string[];
  labels: ScanLabels;
  fieldLabels: FieldLabels;
  onRetake: () => void;
  onLeadCreated: (leadId?: string) => void;
}

function fieldValue(
  scanResult: ScanBusinessCardResultDto | null,
  key: ScanFieldKey,
): string {
  return scanResult?.[key]?.value?.trim() ?? '';
}

function fieldWasFound(
  scanResult: ScanBusinessCardResultDto | null,
  key: ScanFieldKey,
): boolean {
  return scanResult?.[key]?.status === 'found';
}

/** Strip legacy lines that older scans folded into notes. */
function stripLegacyScanNoteLines(notes: string): string {
  return notes
    .split('\n')
    .filter((line) => !/^\s*(Address|Position|Website):\s*/i.test(line))
    .join('\n')
    .replace(/^\n+|\n+$/g, '')
    .trim();
}

function formFromScanResult(scanResult: ScanBusinessCardResultDto) {
  return {
    firstName: fieldValue(scanResult, 'firstName'),
    lastName: fieldValue(scanResult, 'lastName'),
    email: fieldValue(scanResult, 'email'),
    phone: fieldValue(scanResult, 'phone'),
    companyName: fieldValue(scanResult, 'companyName'),
    jobTitle: fieldValue(scanResult, 'jobTitle'),
    website: fieldValue(scanResult, 'website'),
    address: fieldValue(scanResult, 'address'),
    notes: stripLegacyScanNoteLines(scanResult.suggestedMessage ?? ''),
  };
}

export function BusinessCardScanReviewDialog({
  open,
  onOpenChange,
  scanResult,
  capture,
  existingLeads = [],
  profileId,
  workspaceId,
  eventIds,
  labels,
  fieldLabels,
  onRetake,
  onLeadCreated,
}: BusinessCardScanReviewDialogProps) {
  const dict = useDictionary();
  const locale = useLocale();
  const phoneLabels = locale === 'de' ? de : en;
  const [isSaving, setIsSaving] = useState(false);
  const [capturePickerOpen, setCapturePickerOpen] = useState(false);
  const [capturePickerValue, setCapturePickerValue] = useState('');
  const pendingCreateWithEventsRef = useRef<
    ((resolvedEventIds: string[] | undefined) => Promise<void>) | null
  >(null);
  const duplicateDict = dict.leads.duplicate as Record<string, unknown>;

  const { candidates: activeCaptureEvents } = useActiveCaptureEvents(
    workspaceId ?? undefined,
    Boolean(workspaceId) && (open || capturePickerOpen),
  );

  const duplicateLabels = useMemo(
    () => ({
      title: String(duplicateDict.title),
      description: String(duplicateDict.description),
      selectLead: String(duplicateDict.selectLead),
      matchReason: (duplicateDict.matchReason ?? {}) as Record<
        'email' | 'phone' | 'nameCompany',
        string
      >,
      chooseAction: String(duplicateDict.chooseAction ?? 'What should happen?'),
      useExisting: String(duplicateDict.useExisting ?? 'Use this lead'),
      useExistingDescription: String(
        duplicateDict.useExistingDescription ??
          'Keep the existing contact as it is and continue.',
      ),
      supplement: String(duplicateDict.supplement),
      supplementDescription: String(duplicateDict.supplementDescription),
      replace: String(duplicateDict.replace),
      replaceDescription: String(duplicateDict.replaceDescription),
      createNew: String(duplicateDict.createNew ?? 'Save as new lead'),
      createNewDescription: String(
        duplicateDict.createNewDescription ??
          'Just create a new lead — the existing one stays unchanged.',
      ),
      moreOptions: String(duplicateDict.moreOptions ?? 'Change existing lead…'),
      continue: String(duplicateDict.continue ?? 'Continue'),
      cancel: String(duplicateDict.cancel ?? duplicateDict.discard ?? 'Cancel'),
      discard: String(duplicateDict.discard ?? 'Cancel'),
      processing: String(duplicateDict.processing),
      supplementSuccess: String(duplicateDict.supplementSuccess),
      replaceSuccess: String(duplicateDict.replaceSuccess),
      createNewSuccess: String(
        duplicateDict.createNewSuccess ?? 'Saved as new lead',
      ),
      useExistingSuccess: String(
        duplicateDict.useExistingSuccess ?? 'Using existing lead',
      ),
      actionFailed: String(duplicateDict.actionFailed),
    }),
    [duplicateDict],
  );

  const getStatusLabel = (status: string) =>
    dict.leads.status[status as keyof typeof dict.leads.status] || status;

  const { attemptCreate, dialogProps } = useLeadDuplicateResolution({
    existingLeads,
    labels: duplicateLabels,
    getStatusLabel,
    onResolved: (leadId) => {
      onOpenChange(false);
      onLeadCreated(leadId);
    },
  });
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    jobTitle: '',
    website: '',
    address: '',
    notes: '',
  });

  const [prevScanResult, setPrevScanResult] =
    useState<ScanBusinessCardResultDto | null>(null);
  if (open && scanResult && scanResult !== prevScanResult) {
    setPrevScanResult(scanResult);
    setForm(formFromScanResult(scanResult));
  } else if (!open && prevScanResult !== null) {
    setPrevScanResult(null);
  }

  const fieldRows = useMemo(
    () =>
      (
        [
          'firstName',
          'lastName',
          'email',
          'phone',
          'companyName',
          'jobTitle',
          'website',
          'address',
        ] as const
      ).map((key) => ({
        key,
        label: fieldLabels[key],
        found: fieldWasFound(scanResult, key),
        required: key === 'firstName',
      })),
    [fieldLabels, scanResult],
  );

  const handleSave = async () => {
    if (!form.firstName.trim()) {
      void triggerErrorHaptic();
      toast.error(labels.firstNameRequired);
      return;
    }

    const noteParts = [
      stripLegacyScanNoteLines(form.notes),
      form.website.trim() ? form.website.trim() : '',
    ].filter(Boolean);
    const message = noteParts.join('\n\n');
    const runCreate = async (resolvedEventIds: string[] | undefined) => {
      try {
        setIsSaving(true);

        let attachments:
          | Array<{
              fileId: string;
              kind: LeadAttachmentKindValue;
              mimeType: string;
              title: string;
            }>
          | undefined;

        if (capture) {
          try {
            const ext = extensionFromMimeType(capture.mimeType);
            const file = base64ToFile(
              capture.imageBase64,
              capture.mimeType,
              `business-card.${ext}`,
            );
            const uploadResponse = await FilesApi.uploadFile({
              body: { file },
            });
            const fileId = uploadResponse.data?.file?.id;
            if (fileId) {
              const attachmentTitle =
                (dict.leads.detail.attachments?.businessCard as string) ||
                'Business card';
              attachments = [
                {
                  fileId,
                  kind: LeadAttachmentKind.BUSINESS_CARD,
                  mimeType: capture.mimeType,
                  title: attachmentTitle,
                },
              ];
            }
          } catch (uploadError) {
            console.error('Failed to upload business card image:', uploadError);
            toast.warning(labels.uploadFailed, {
              description: labels.saveWithoutAttachment,
            });
          }
        }

        const leadId = await attemptCreate({
          ...(profileId ? { profileId } : {}),
          ...(workspaceId ? { workspaceId } : {}),
          firstName: form.firstName.trim(),
          ...(form.lastName.trim() && { lastName: form.lastName.trim() }),
          ...(form.email.trim() && { email: form.email.trim() }),
          ...(form.phone.trim() && { phone: form.phone.trim() }),
          ...(form.companyName.trim() && {
            companyName: form.companyName.trim(),
          }),
          ...(form.jobTitle.trim() && { jobTitle: form.jobTitle.trim() }),
          ...(form.address.trim() && { address: form.address.trim() }),
          ...(message && { message }),
          ...(attachments?.length ? { attachments } : {}),
          status: LeadStatus.NEW,
          source: LeadSource.BUSINESS_CARD,
          ...(resolvedEventIds?.length ? { eventIds: resolvedEventIds } : {}),
        });

        if (leadId) {
          void triggerSuccessHaptic();
          onOpenChange(false);
          onLeadCreated(leadId);
        }
      } catch (error) {
        console.error('Failed to create lead from business card:', error);
        void triggerErrorHaptic();
        toast.error(labels.saveFailed);
      } finally {
        setIsSaving(false);
      }
    };

    if (eventIds?.length) {
      await runCreate(eventIds);
      return;
    }

    if (activeCaptureEvents.length > 1) {
      pendingCreateWithEventsRef.current = runCreate;
      setCapturePickerValue(activeCaptureEvents[0]!.id);
      setCapturePickerOpen(true);
      return;
    }

    if (activeCaptureEvents.length === 1) {
      await runCreate([activeCaptureEvents[0]!.id]);
      return;
    }

    await runCreate(undefined);
  };

  const capturePickerDict = (
    dict.leads.events as {
      capturePicker?: {
        title: string;
        none: string;
        confirm: string;
        cancel: string;
      };
    }
  ).capturePicker;

  return (
    <>
      <LeadDuplicateResolutionDialog {...dialogProps} />
      <LeadEventCapturePicker
        open={capturePickerOpen}
        onOpenChange={(next) => {
          setCapturePickerOpen(next);
          if (!next) pendingCreateWithEventsRef.current = null;
        }}
        events={activeCaptureEvents}
        value={capturePickerValue}
        onChange={setCapturePickerValue}
        onConfirm={() => {
          const run = pendingCreateWithEventsRef.current;
          const picked = capturePickerValue;
          setCapturePickerOpen(false);
          pendingCreateWithEventsRef.current = null;
          const ids =
            picked === LEAD_EVENT_CAPTURE_NONE || !picked
              ? undefined
              : [picked];
          void run?.(ids);
        }}
        labels={{
          title:
            capturePickerDict?.title ?? 'Zu welchem Event gehört der Lead?',
          none: capturePickerDict?.none ?? 'Keinem Event',
          confirm: capturePickerDict?.confirm ?? 'Weiter',
          cancel: capturePickerDict?.cancel ?? 'Abbrechen',
        }}
      />
      <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent className="flex max-h-[90vh] w-full flex-col gap-0 p-0 sm:max-w-lg">
          <ResponsiveDialogHeader className="space-y-1 px-6 pt-6">
            <ResponsiveDialogTitle>{labels.title}</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {labels.description}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            <div className="flex flex-wrap gap-2">
              {fieldRows.map((row) => (
                <Badge
                  key={row.key}
                  variant="outline"
                  className={cn(
                    'gap-1 font-normal',
                    row.found
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-400',
                  )}
                >
                  {row.found ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <AlertCircle className="h-3 w-3" />
                  )}
                  {row.label}: {row.found ? labels.found : labels.missing}
                </Badge>
              ))}
            </div>

            <p className="text-xs text-muted-foreground">
              {labels.requiredHint}
            </p>

            <div className="grid gap-3">
              {fieldRows.map((row) => (
                <div key={row.key} className="grid gap-1.5">
                  <Label htmlFor={`bcc-${row.key}`} className="text-sm">
                    {row.label}
                    {row.required ? ' *' : ''}
                  </Label>
                  {row.key === 'phone' ? (
                    <PhoneInput
                      id={`bcc-${row.key}`}
                      defaultCountry="DE"
                      labels={phoneLabels}
                      value={form.phone || undefined}
                      onChange={(v) => setForm({ ...form, phone: v || '' })}
                    />
                  ) : (
                    <Input
                      id={`bcc-${row.key}`}
                      type={row.key === 'email' ? 'email' : 'text'}
                      inputMode={
                        row.key === 'website'
                          ? 'url'
                          : row.key === 'email'
                            ? 'email'
                            : undefined
                      }
                      autoComplete={
                        row.key === 'website'
                          ? 'url'
                          : row.key === 'email'
                            ? 'email'
                            : undefined
                      }
                      autoCapitalize={
                        row.key === 'website' || row.key === 'email'
                          ? 'none'
                          : undefined
                      }
                      autoCorrect={
                        row.key === 'website' || row.key === 'email'
                          ? 'off'
                          : undefined
                      }
                      spellCheck={
                        row.key === 'website' || row.key === 'email'
                          ? false
                          : undefined
                      }
                      value={form[row.key]}
                      onChange={(e) =>
                        setForm({ ...form, [row.key]: e.target.value })
                      }
                      className={cn(
                        row.required &&
                          !form.firstName.trim() &&
                          row.key === 'firstName' &&
                          'border-destructive/60',
                      )}
                    />
                  )}
                </div>
              ))}

              <div className="grid gap-1.5">
                <Label htmlFor="bcc-notes" className="text-sm">
                  {fieldLabels.notes}
                </Label>
                <Textarea
                  id="bcc-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <ResponsiveDialogFooter className="flex-col gap-2 border-t px-6 py-4 sm:flex-col">
            <Button
              type="button"
              className="w-full"
              disabled={isSaving || !form.firstName.trim()}
              onClick={() => void handleSave()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {labels.saving}
                </>
              ) : (
                labels.save
              )}
            </Button>
            <div className="flex w-full gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 gap-2"
                onClick={onRetake}
                disabled={isSaving}
              >
                <RotateCcw className="h-4 w-4" />
                {labels.retake}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                {labels.cancel}
              </Button>
            </div>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
}
